/**
 * FoundingLightReserveModal — Feb 2026
 * ───────────────────────────────────────────────────────────────────
 * While Stripe activation is paused, the "Reserve my Founding Light"
 * CTA on the Soulumo landing opens this modal. It captures intent
 * (email + optional note) into `founding_light_reservations` so the
 * moment Stripe is live we have a warm list to convert.
 *
 * Authed users: email is pre-filled and locked.
 * Anonymous users: email is required.
 * After submit: confirmation state replaces the form.
 */
import React, { useEffect, useRef, useState } from "react";

const TOK = {
  ink: "#050a17",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.86)",
  goldMist: "rgba(224,169,122,0.22)",
};

const API = process.env.REACT_APP_BACKEND_URL || "";

function readLocalUser() {
  try {
    const raw = localStorage.getItem("reclaim_user");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export default function FoundingLightReserveModal({ isAuthed, onClose }) {
  const localUser = isAuthed ? readLocalUser() : null;
  const [email, setEmail] = useState((localUser && localUser.email) || "");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const emailRef = useRef(null);

  // Autofocus the first useful field and lock body scroll.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      if (emailRef.current) emailRef.current.focus();
    }, 50);
    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, []);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    const cleanEmail = (email || "").trim().toLowerCase();
    if (!cleanEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleanEmail)) {
      setErr("Please add a valid email so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      const headers = { "Content-Type": "application/json" };
      const token = localStorage.getItem("reclaim_token");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const r = await fetch(`${API}/api/founding-light/reserve`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email: cleanEmail,
          note: note.trim() || undefined,
          name: (localUser && localUser.name) || undefined,
          source: "soulumo-landing",
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not reserve — please try again.");
      }
      setDone(true);
    } catch (e) {
      setErr(e.message || "Could not reserve — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="founding-light-reserve-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 sm:p-7"
        style={{
          background: "linear-gradient(180deg, rgba(10,20,40,0.98) 0%, rgba(5,11,26,1) 100%)",
          border: `1px solid ${TOK.goldMist}`,
          color: TOK.cream,
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: "0 30px 90px rgba(0,0,0,0.55), 0 0 60px rgba(224,169,122,0.08)",
        }}
      >
        <div
          className="uppercase"
          style={{
            color: TOK.goldSoft,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
            letterSpacing: "0.36em",
          }}
        >
          ✦ Soulumo Founding Light ✦
        </div>

        {!done ? (
          <>
            <h3
              className="mt-2 italic font-light"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1.5rem, 5vw, 1.85rem)",
                lineHeight: 1.2,
              }}
            >
              Hold my place.
            </h3>
            <p
              className="mt-2 italic"
              style={{
                color: TOK.creamDim,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "0.98rem",
                lineHeight: 1.55,
              }}
            >
              No charge today. We&apos;ll quietly email you the moment
              Founding Light opens — £6.99 / month (was £9.99) or £59.99 / year,
              kept for as long as you stay.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="founding-light-reserve-form">
              <div>
                <label
                  className="block uppercase mb-1.5"
                  style={{
                    color: TOK.creamMist,
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.6rem",
                    letterSpacing: "0.28em",
                  }}
                >
                  Email
                </label>
                <input
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!!(localUser && localUser.email)}
                  placeholder="you@yourname.com"
                  data-testid="founding-light-reserve-email"
                  style={{
                    width: "100%",
                    background: "rgba(0,0,0,0.35)",
                    border: `1px solid ${TOK.goldMist}`,
                    color: TOK.cream,
                    borderRadius: 12,
                    padding: "12px 14px",
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.95rem",
                    outline: "none",
                    opacity: localUser && localUser.email ? 0.7 : 1,
                  }}
                />
              </div>

              <div>
                <label
                  className="block uppercase mb-1.5"
                  style={{
                    color: TOK.creamMist,
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.6rem",
                    letterSpacing: "0.28em",
                  }}
                >
                  Anything you want me to know? (optional)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. For my mum, who struggles to sleep."
                  data-testid="founding-light-reserve-note"
                  style={{
                    width: "100%",
                    background: "rgba(0,0,0,0.35)",
                    border: `1px solid ${TOK.goldMist}`,
                    color: TOK.cream,
                    borderRadius: 12,
                    padding: "12px 14px",
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.92rem",
                    resize: "vertical",
                    minHeight: 84,
                    outline: "none",
                  }}
                />
              </div>

              {err && (
                <div
                  className="text-sm italic"
                  style={{ color: "#f0a0a0", fontFamily: '"Cormorant Garamond", serif' }}
                  data-testid="founding-light-reserve-error"
                >
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="founding-light-reserve-submit"
                className="w-full rounded-full py-3 disabled:opacity-60 transition-all"
                style={{
                  background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                  border: "1px solid #f5e0c5",
                  color: "#1a0f05",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.78rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  minHeight: 48,
                  boxShadow: "0 0 36px rgba(224,169,122,0.3)",
                  cursor: submitting ? "wait" : "pointer",
                }}
              >
                {submitting ? "Reserving…" : "Reserve my place"}
              </button>

              <button
                type="button"
                onClick={onClose}
                data-testid="founding-light-reserve-cancel"
                className="w-full rounded-full py-2 italic"
                style={{
                  background: "transparent",
                  border: `1px solid ${TOK.goldMist}`,
                  color: TOK.creamMist,
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "0.86rem",
                  cursor: "pointer",
                }}
              >
                Not now
              </button>
            </form>
          </>
        ) : (
          <div data-testid="founding-light-reserve-success">
            <h3
              className="mt-2 italic font-light"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1.5rem, 5vw, 1.85rem)",
                lineHeight: 1.2,
              }}
            >
              Your place is held.
            </h3>
            <p
              className="mt-3 italic"
              style={{
                color: TOK.creamDim,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1rem",
                lineHeight: 1.6,
              }}
            >
              Thank you. The moment Founding Light opens, I&apos;ll
              email <strong style={{ color: TOK.gold }}>{email}</strong>{" "}
              personally — your Founding Light price kept for you.
            </p>
            <p
              className="mt-3 italic"
              style={{
                color: TOK.creamMist,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "0.92rem",
                lineHeight: 1.6,
              }}
            >
              In the meantime — try Soulumo free with the beta code{" "}
              <code
                style={{
                  color: TOK.gold,
                  fontFamily: '"Manrope", monospace',
                  letterSpacing: "0.1em",
                  background: "rgba(224,169,122,0.1)",
                  padding: "1px 8px",
                  borderRadius: 6,
                }}
              >
                SOULUMO-BETA
              </code>
              .
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="founding-light-reserve-close"
              className="mt-6 w-full rounded-full py-3 transition-all"
              style={{
                background: "transparent",
                border: `1px solid ${TOK.gold}`,
                color: TOK.gold,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.76rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                fontWeight: 500,
                minHeight: 44,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
